import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EDITOR_ROLES, SUPERVISOR_ROLES } from "@shared/core/types";
import { CHECKLIST_STATUS_LABELS, DOCUMENT_STATUS_LABELS, type CandidateDocument, type ChecklistItem, type DocumentType } from "@shared/modules/x-documents";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Badge } from "../../../core/components/ui/badge";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { allowedFor } from "../../../core/modules";
import { moduleEnabled } from "../../../core/blueprint";
import { candidateDocuments, deleteDocument, downloadUrl, formatSize, listTypes, requestReading, setChecklist, updateDocument, uploadToCandidate } from "../lib/api";

const CHECK_VARIANT = { manquante: "danger", recue: "warning", validee: "success" } as const;
const DOC_VARIANT = { recue: "warning", validee: "success", rejetee: "danger" } as const;

export function CandidateDocumentsPage() {
  const { id = "" } = useParams();
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const [docs, setDocs] = useState<CandidateDocument[]>([]);
  const [checklist, setChecklistState] = useState<ChecklistItem[]>([]);
  const [types, setTypes] = useState<DocumentType[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [typeCode, setTypeCode] = useState("autre");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [editChecklist, setEditChecklist] = useState(false);
  const [required, setRequired] = useState<string[]>([]);
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role);
  const canDelete = allowedFor({ roles: SUPERVISOR_ROLES }, user?.role);
  const canUpload = canEdit || user?.role === "avocat";
  const canRead = canEdit && moduleEnabled("x-extraction");

  const load = useCallback(async () => {
    try {
      const r = await candidateDocuments(id);
      setDocs(r.documents);
      setChecklistState(r.checklist);
      setRequired(r.checklist.map((c) => c.typeCode));
    } catch {
      push("danger", "Pièces introuvables");
    }
  }, [id, push]);

  useEffect(() => {
    void load();
    listTypes().then(setTypes).catch(() => setTypes([]));
  }, [load]);

  const selectedType = types.find((t) => t.code === typeCode);

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const r = await uploadToCandidate(id, files, typeCode, selectedType?.expires ? expiresAt : undefined);
      if (r.created.length) push("success", `${r.created.length} pièce(s) déposée(s)`);
      for (const rej of r.rejected) push("warning", `${rej.name} : format non accepté (PDF, Word, image)`);
      setFiles([]);
      setExpiresAt("");
      await load();
    } catch {
      push("danger", "Dépôt impossible");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (d: CandidateDocument, status: "validee" | "rejetee" | "recue") => {
    setBusy(true);
    try {
      await updateDocument(d.id, { status });
      push("success", `Pièce ${DOCUMENT_STATUS_LABELS[status].toLowerCase()}`);
      await load();
    } catch {
      push("danger", "Modification impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Pièces du dossier"
        subtitle="Classement par type, liste de contrôle, dates d'expiration."
        actions={
          <Link className="btn btn--ghost" to={`/candidats/${id}`}>
            <span className="btn__icon">
              <Icon name="chevron-left" size={16} />
            </span>
            Retour à la fiche
          </Link>
        }
      />

      <div className="pieces">
        <Card
          title="Liste de contrôle"
          subtitle="Pièces attendues pour ce dossier."
          actions={
            canEdit ? (
              <Button variant="ghost" size="sm" onClick={() => setEditChecklist((v) => !v)}>
                {editChecklist ? "Fermer" : "Ajuster"}
              </Button>
            ) : undefined
          }
        >
          {editChecklist ? (
            <div className="pieces__checklist-edit">
              {types.map((t) => (
                <label key={t.code} className="pieces__check">
                  <input
                    type="checkbox"
                    checked={required.includes(t.code)}
                    onChange={(e) => setRequired(e.target.checked ? [...required, t.code] : required.filter((c) => c !== t.code))}
                  />
                  {t.label}
                </label>
              ))}
              <Button
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    setChecklistState(await setChecklist(id, required));
                    setEditChecklist(false);
                    push("success", "Liste mise à jour");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Enregistrer
              </Button>
            </div>
          ) : (
            <ul className="pieces__checklist">
              {checklist.map((c) => (
                <li key={c.typeCode}>
                  <span>{c.label}</span>
                  <span>
                    {c.expiresAt ? <span className="muted">expire le {c.expiresAt} · </span> : null}
                    <Badge variant={CHECK_VARIANT[c.status]}>{CHECKLIST_STATUS_LABELS[c.status]}</Badge>
                  </span>
                </li>
              ))}
              {checklist.length === 0 ? <li className="muted">Aucune pièce attendue.</li> : null}
            </ul>
          )}
        </Card>

        {canUpload ? (
          <Card title="Déposer des pièces" subtitle="PDF, Word, numérisations ou photos (JPEG, PNG, HEIC, TIFF), 40 Mo par fichier.">
            <div className="form-grid">
              <Field label="Type de pièce">
                <Select value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
                  {types.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>
              {selectedType?.expires ? (
                <Field label="Date d'expiration">
                  <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                </Field>
              ) : null}
              <div className="form-grid__full">
                <Field label="Fichiers" hint="Plusieurs fichiers à la fois (lot de pages numérisées, photos).">
                  <Input type="file" multiple accept=".pdf,.docx,image/*" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
                </Field>
              </div>
            </div>
            <div className="pieces__upload-actions">
              <Button variant="primary" disabled={busy || files.length === 0} onClick={upload} icon={<Icon name="arrow-up" size={16} />}>
                Déposer {files.length ? `(${files.length})` : ""}
              </Button>
            </div>
          </Card>
        ) : null}
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Pièce</th>
            <th>Type</th>
            <th>Statut</th>
            <th className="table__col--wide">Déposée</th>
            <th className="table__col--wide">Lecture</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id}>
              <td>
                <a href={downloadUrl(d.id, true)} target="_blank" rel="noreferrer">
                  {d.originalName}
                </a>
                <span className="muted"> · {formatSize(d.size)}</span>
              </td>
              <td>
                {types.find((t) => t.code === d.typeCode)?.label ?? d.typeCode}
                {d.expiresAt ? <span className="muted"> · exp. {d.expiresAt}</span> : null}
              </td>
              <td>
                <Badge variant={DOC_VARIANT[d.status]}>{DOCUMENT_STATUS_LABELS[d.status]}</Badge>
              </td>
              <td className="table__col--wide">
                {formatDateTime(d.uploadedAt, { dateStyle: "short", timeStyle: "short" })}
                {d.uploadedByName ? <span className="muted"> · {d.uploadedByName}</span> : null}
              </td>
              <td className="table__col--wide">
                {d.textStatus === "fait" ? <Badge variant="success">texte lu</Badge> : d.textStatus === "echec" ? <Badge variant="danger">illisible</Badge> : <span className="muted">—</span>}
              </td>
              <td className="table__actions">
                <a href={downloadUrl(d.id)}>Télécharger</a>{" "}
                {canEdit && d.status !== "validee" ? (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => patch(d, "validee")}>
                    Valider
                  </Button>
                ) : null}{" "}
                {canEdit && d.status !== "rejetee" ? (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => patch(d, "rejetee")}>
                    Rejeter
                  </Button>
                ) : null}{" "}
                {canRead ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={async () => {
                      await requestReading(d.id, d.typeCode === "cv" ? "cv" : "piece");
                      push("info", "Lecture lancée : la proposition apparaîtra dans « Importer des CV »");
                    }}
                  >
                    Lire
                  </Button>
                ) : null}{" "}
                {canDelete ? (
                  <Button
                    variant="ghost-danger"
                    size="sm"
                    disabled={busy}
                    onClick={async () => {
                      if (!window.confirm(`Supprimer « ${d.originalName} » ?`)) return;
                      await deleteDocument(d.id);
                      await load();
                    }}
                  >
                    Supprimer
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
          {docs.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Aucune pièce déposée.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
